import React, { useState } from "react";
import {
  changeUserKeys,
  fetchUserKeys,
} from "../../services/apicustomerreportservice";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import { 
  KeyRound, 
  X, 
  User, 
  Lock, 
  RefreshCw, 
  ShieldAlert, 
  CheckCircle2, 
  Loader2 
} from "lucide-react";

export default function ApiKeyModal({ isOpen, onClose }) {
  // Component States
  const [userId, setUserId] = useState("");
  const [adminPin, setAdminPin] = useState("");

  const [fetchedData, setFetchedData] = useState(null);
  const [changedData, setChangedData] = useState(null);

  const [isChanging, setIsChanging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Do not render anything if modal is not open
  if (!isOpen) return null;

  // Handle closing and resetting the modal state
  const handleClose = () => {
    setUserId("");
    setAdminPin("");
    setFetchedData(null);
    setChangedData(null);
    setIsChanging(false);
    onClose();
  };

  const handleFetchKey = async (e) => {
    e.preventDefault();
    if (!userId) return;

    setIsLoading(true);
    try {
      const res = await fetchUserKeys({ user_id: userId });

      if (res?.status === true) {
        setFetchedData(res?.data);
        showSuccessToast(res?.message);
      } else {
        const err = res?.error || res?.data?.errors;

        const errMsg = Object.values(err || {})[0]?.[0];

        showErrorToast(res?.message || res?.data?.message || errMsg);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeKeySubmit = async (e) => {
    e.preventDefault();
    if (!adminPin) return;

    setIsLoading(true);

    try {
      const res = await changeUserKeys({
        user_id: userId,
        admin_pin: adminPin,
      });

      if (res) {
        console.log(res);
      }

      if (res?.status === true) {
        setChangedData(res?.data);
        showSuccessToast(res?.message);
      } else {
        const err = res?.error || res?.data?.errors;

        const errMsg = Object.values(err || {})[0]?.[0];

        showErrorToast(res?.message || res?.data?.message || errMsg);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
   <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 transition-all">
  {/* Modal Container */}
  <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl border border-slate-100 overflow-hidden transform transition-all">
    
    {/* Header */}
    <div className="flex justify-between items-center px-6 py-4 border-b border-slate-100 bg-slate-50/50">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-blue-50 text-blue-600 rounded-xl">
          <KeyRound className="w-5 h-5" />
        </div>
        <div>
          <h2 className="text-slate-800 font-semibold text-lg leading-tight">
            API Key Management
          </h2>
          <p className="text-xs text-slate-500">Manage and rotate credentials</p>
        </div>
      </div>
      <button
        onClick={handleClose}
        className="text-slate-400 hover:text-slate-600 hover:bg-slate-100 p-2 rounded-full transition-colors"
        title="Close"
      >
        <X className="w-4 h-4" />
      </button>
    </div>

    {/* Body */}
    <div className="p-6 flex flex-col gap-4">
      {/* STEP 1: Fetch User API Key */}
      {!fetchedData && !changedData && (
        <form onSubmit={handleFetchKey} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              User ID
            </label>
            <div className="relative flex items-center">
              <User className="absolute left-3.5 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="e.g. APID1025"
                value={userId}
                onChange={(e) => setUserId(e.target.value.toUpperCase())}
                className="w-full uppercase pl-10 pr-3.5 py-2.5 text-sm bg-slate-50/50 border border-slate-200 rounded-xl font-medium placeholder:text-slate-400 text-slate-800 focus:outline-none focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-500/10 transition-all"
                required
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-medium rounded-xl px-4 py-2.5 text-sm shadow-sm transition-all disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Fetching...</span>
              </>
            ) : (
              <span>Fetch API Key</span>
            )}
          </button>
        </form>
      )}

      {/* STEP 2: Show Fetched Data & "Change Key" Action */}
      {fetchedData && !changedData && (
        <div className="flex flex-col gap-4">
          <div className="bg-slate-50 border border-slate-200/70 rounded-xl p-4 flex flex-col gap-3">
            <div className="flex justify-between items-center pb-2 border-b border-slate-200/60">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                User Name
              </span>
              <span className="font-semibold text-slate-800 text-sm">
                {fetchedData.user_name}
              </span>
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Current API Key
              </span>
              <code className="bg-slate-900 text-slate-100 px-3.5 py-2.5 rounded-lg text-xs font-mono break-all border border-slate-800 shadow-inner">
                {fetchedData.api_key}
              </code>
            </div>
          </div>

          {!isChanging ? (
            <button
              onClick={() => setIsChanging(true)}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl px-4 py-2.5 text-sm shadow-sm transition-all flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Change API Key</span>
            </button>
          ) : (
            /* STEP 3: Admin PIN Input */
            <form
              onSubmit={handleChangeKeySubmit}
              className="flex flex-col gap-3 bg-amber-50/50 border border-amber-200/70 p-4 rounded-xl"
            >
              <div className="flex items-center gap-2 text-amber-800 font-medium text-xs uppercase tracking-wider">
                <ShieldAlert className="w-4 h-4 text-amber-600" />
                <span>Enter Admin PIN to Confirm</span>
              </div>
              <div className="relative flex items-center">
                <Lock className="absolute left-3.5 w-4 h-4 text-slate-400" />
                <input
                  type="password"
                  maxLength={6}
                  placeholder="••••••"
                  value={adminPin}
                  onChange={(e) => setAdminPin(e.target.value)}
                  className="w-full pl-10 pr-3.5 py-2 text-sm bg-white border border-slate-200 rounded-lg text-slate-800 focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 transition-all"
                  required
                />
              </div>
              <div className="flex gap-2.5 pt-1">
                <button
                  type="button"
                  onClick={() => setIsChanging(false)}
                  className="w-1/3 bg-slate-100 text-slate-700 hover:bg-slate-200 rounded-lg px-3 py-2 text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-2/3 bg-amber-600 hover:bg-amber-700 text-white rounded-lg px-3 py-2 text-sm font-medium shadow-sm transition-all disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Changing...</span>
                    </>
                  ) : (
                    <span>Confirm Change</span>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* STEP 4: Success State (Changed API Key) */}
      {changedData && (
        <div className="flex flex-col gap-4">
          <div className="bg-emerald-50 border border-emerald-200/80 p-3.5 rounded-xl text-center flex items-center justify-center gap-2 text-emerald-800 font-medium text-sm">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
            <span>API Key Successfully Rotated</span>
          </div>

          <div className="bg-slate-50 border border-slate-200/80 p-4 rounded-xl flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                New API Key
              </span>
              <code className="bg-slate-900 text-lg text-emerald-400 border border-slate-800 px-3.5 py-2.5 rounded-lg font-mono break-all shadow-inner">
                {changedData.api_key}
              </code>
            </div>
            <div className="flex justify-between items-center text-xs text-slate-500 pt-1 border-t border-slate-200/60">
              <span>Updated At</span>
              <span className="font-medium text-slate-700">
                {changedData.changed_at}
              </span>
            </div>
          </div>

          <button
            onClick={handleClose}
            className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-xl px-4 py-2.5 text-sm font-medium transition-all shadow-sm"
          >
            Done
          </button>
        </div>
      )}
    </div>
  </div>
</div>
  );
}
